import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type Background,
  backgroundStyle,
  type Look,
  lookProps,
  lookSurface,
} from '@/entities/appearance';
import {
  type Board,
  boardColor,
  useBoardBackgroundImage,
  useSetBoardAppearance,
} from '@/entities/board';
import {
  BackgroundEditor,
  isLookValid,
  LookEditor,
  lookPayload,
  useBackgroundImageDraft,
} from '@/features/appearance-editor';
import { FormError } from '@/shared/forms';
import { Button, Dialog, HelpRow } from '@/shared/ui';

import styles from './BoardAppearanceDialog.module.css';
import { TILE_STYLES } from './tile-styles';

type Tab = 'tile' | 'background';

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

interface Props {
  board: Board;
  onClose: () => void;
}

/**
 * How the board looks: its tile on the workspace page and the background of the opened board.
 * The board's semantic color (shown as a strip and used in calendars) is separate and always
 * stays visible. Save/Cancel with a live preview of both.
 */
export function BoardAppearanceDialog({ board, onClose }: Props) {
  const { t } = useTranslation();
  const setAppearance = useSetBoardAppearance(board.id);
  const backgroundImage = useBoardBackgroundImage(board.id);
  const initialTile = board.appearance?.tile ?? null;
  const initialBackground = board.appearance?.background ?? null;
  const [tab, setTab] = useState<Tab>('tile');
  const [tile, setTile] = useState<Look | null>(initialTile);
  const [background, setBackground] = useState<Background | null>(initialBackground);
  const image = useBackgroundImageDraft(board.background_image_url);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const hasImage = image.previewUrl !== null;
  const effectiveBackground = background?.kind === 'image' && !hasImage ? null : background;
  const tilePayload = lookPayload(tile);
  const tileProps = lookProps(tilePayload);
  const backgroundPreview = backgroundStyle(effectiveBackground, image.previewUrl);

  const save = async () => {
    setPending(true);
    setError(null);
    try {
      if (image.file) {
        await backgroundImage.upload.mutateAsync(image.file);
      } else if (image.removed && board.background_image_url) {
        await backgroundImage.remove.mutateAsync();
      }
      const next = { tile: tilePayload, background: effectiveBackground };
      const before = { tile: initialTile, background: initialBackground };
      if (!same(next, before) || image.file || image.removed) {
        await setAppearance.mutateAsync({
          v: 1,
          tile: tilePayload,
          background: effectiveBackground,
        });
      }
      onClose();
    } catch (reason) {
      setError(reason);
      setPending(false);
    }
  };

  return (
    <Dialog title={t('boards.appearanceTitle', { name: board.name })} onClose={onClose} size="lg">
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <div className={styles.previews}>
          <div
            className={`${styles.tilePreview} ${lookSurface ?? ''}`}
            data-testid="board-tile-preview"
            aria-label={t('boards.previewTile')}
            role="img"
            {...tileProps}
            style={{ ...tileProps.style, ['--board-color' as string]: boardColor(board) }}
          >
            <span className={styles.tileColor} aria-hidden />
            <strong>{board.name}</strong>
          </div>
          <div
            className={styles.pagePreview}
            data-testid="board-background-preview"
            aria-label={t('boards.previewBackground')}
            role="img"
            style={backgroundPreview.style}
          >
            <span className={styles.pageColumn} />
            <span className={styles.pageColumn} />
          </div>
        </div>

        <HelpRow topic={t('boards.appearance')} topicId="boards" text={t('help.boardAppearance')}>
          <div className={styles.tabs} role="tablist" aria-label={t('boards.appearanceSections')}>
            {(['tile', 'background'] as const).map((value) => (
              <button
                key={value}
                type="button"
                role="tab"
                className={`${styles.tab} ${tab === value ? styles.tabOn : ''}`}
                aria-selected={tab === value}
                onClick={() => {
                  setTab(value);
                }}
              >
                {t(`boards.sections.${value}`)}
              </button>
            ))}
          </div>
        </HelpRow>

        {tab === 'tile' ? (
          <>
            <p className={styles.hint}>{t('boards.tileHint')}</p>
            <LookEditor value={tile} onChange={setTile} allowed={TILE_STYLES} idPrefix="tile" />
          </>
        ) : (
          <>
            <p className={styles.hint}>{t('boards.backgroundHint')}</p>
            <BackgroundEditor
              value={background}
              onChange={setBackground}
              image={image}
              idPrefix="board"
            />
          </>
        )}

        <FormError error={error} />
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={pending || !isLookValid(tile)}>
            {t('common.save')}
          </Button>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <span className={styles.spacer} />
          <Button
            variant="ghost"
            onClick={() => {
              setTile(null);
              setBackground(null);
              image.remove();
            }}
          >
            {t('boards.resetAppearance')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
